import type { Plan } from '../types';
import { localeFormats, t } from '../i18n';
import type { QuotaRefusal } from '../services/claude';

/**
 * Ce qu'on fait d'un refus de quota, décidé en UN SEUL endroit.
 *
 * Quatre points d'appel traitent un `QuotaError` (useClaude, useCvExport, useInterview,
 * CvCreateScreen). Recopier la règle quatre fois, c'est accepter qu'elle diverge : le jour où l'on
 * ajoutera un plafond au plan Pro, trois copies sur quatre proposeront encore un abonnement à
 * quelqu'un qui a déjà le plus haut.
 */

/** Plans qui n'ont rien au-dessus : leur ouvrir la page des abonnements n'a aucun sens. */
const PLANS_SANS_SUITE: readonly Plan[] = ['pro', 'school'];

/**
 * Faut-il emmener vers la page des abonnements ?
 *
 * NON pour un abonné Pro — c'est le plan individuel le plus haut, lui proposer d'acheter revient à
 * lui vendre ce qu'il paie déjà — ni pour une licence école, qui ne s'achète pas depuis
 * l'application et dont le titulaire n'est pas l'acheteur.
 *
 * Le plan vient du SERVEUR, porté par le refus lui-même : c'est celui qui a réellement servi à
 * refuser. La lecture locale du plan peut être plus ancienne, et trancher dessus ferait dépendre
 * l'affichage d'une course entre deux sources.
 *
 * Plan absent (fonction serveur pas encore redéployée) : on propose, comme avant. Une page de trop
 * pour un Pro se referme ; un compte gratuit laissé devant un mur sans issue, non.
 */
export function proposeAbonnement(plan: Plan | undefined | null): boolean {
  return !plan || !PLANS_SANS_SUITE.includes(plan);
}

/**
 * Date à laquelle le compteur repart, en toutes lettres.
 *
 * C'EST L'INFORMATION QUI ÉVITE LE SENTIMENT D'ÊTRE BLOQUÉ POUR TOUJOURS. « Plus d'utilisations »
 * sans échéance se lit comme une porte fermée ; avec une date, c'est une attente.
 *
 * Le compteur est cloisonné par période UTC au format 'YYYY-MM' (`currentPeriod`, côté serveur) :
 * il repart donc au 1er du mois suivant, à minuit UTC. On repart du PREMIER du mois et non du jour
 * courant — ajouter un mois au 31 janvier donnerait le 3 mars.
 *
 * Lecture et formatage en UTC, comme la période : le 30 septembre à 23 h 30 UTC est déjà le
 * 1er octobre dans certains fuseaux, et formater en heure locale annoncerait alors une remise à
 * zéro un mois trop tôt.
 */
export function prochaineRemiseAZero(now: Date = new Date()): string {
  const premierDuMoisSuivant = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 12, 0, 0),
  );
  // LE JOUR N'EST PAS FORMATÉ PAR `Intl`, ET C'EST VOLONTAIRE. En français, le premier du mois
  // s'écrit « 1er » ; `Intl` rend « 1 octobre 2026 », qui se lit comme une faute. Comme la date est
  // TOUJOURS un premier du mois, le quantième appartient au gabarit de la langue — d'où un mois et
  // une année formatés ici, et le « 1er » / « 1 » posé par la traduction.
  const moisAnnee = new Intl.DateTimeFormat(localeFormats(), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(premierDuMoisSuivant);
  return t('quota.resetDate', { monthYear: moisAnnee });
}

/**
 * Message pour qui n'a RIEN à acheter au-dessus (Pro, licence école).
 *
 * Aucune mention d'abonnement, aucune invitation à passer à un plan supérieur : il n'y en a pas. Ce
 * qui reste utile à dire, c'est la date — et, si la fonctionnalité n'est pas comprise dans la
 * licence, qu'attendre n'y changera rien.
 */
export function messageRefusSansOffre(reason: QuotaRefusal | undefined, now: Date = new Date()): string {
  if (reason === 'not_included') return t('quota.notIncludedNoUpgrade');
  return t('quota.capReachedNoUpgrade', { date: prochaineRemiseAZero(now) });
}
